import { useCallback, useEffect, useRef, useState } from 'react';

import type { Event, RiveEventPayload } from '@rive-app/react-canvas';
import { EventType, Fit, Layout } from '@rive-app/react-canvas';
import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import signUpEmailRiv from '../../../../assets/auth/sign_up_email_v3.riv';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { useRiveMachine } from '../../../../hooks/rive/useRiveMachine';
import { analytics } from '../../../../services/analytics';
import { useFlag } from '../../../../services/launchDarkly';

import styles from './EmailVerificationSuccessRedesign.module.scss';
import { EmailVerificationSuccessStaticContent } from './EmailVerificationSuccessStaticContent';

const STATE_MACHINE_NAME = 'State Machine 1';
const ARTBOARD_NAME = 'Email';
const RIVE_FALLBACK_TIMEOUT_MS = 1000;
const AUTO_CLOSE_DELAY_MS = 3000;

export const EmailVerificationSuccessRedesign = observer(function EmailVerificationSuccessRedesign(
  props: Pick<WizardChildProps, 'isCurrent' | 'nextStep'>,
) {
  const { t } = useTranslation();
  const [showStaticFallback, setShowStaticFallback] = useState(false);
  const isRiveStaticFallbackEnabled = useFlag<boolean>('registration_flow_rive_static_fallback', false);

  const hasClosedRef = useRef(false);

  const onClose = useCallback(() => {
    if (hasClosedRef.current) return;
    if (props.isCurrent) {
      hasClosedRef.current = true;
      const eventParams: AnalyticsEvents['email_verified_success'] = {
        event_category: 'sign_up',
        event_action: 'click',
        button_text: 'close',
      };
      void analytics.sendEvent('email_verified_success', eventParams);

      props.nextStep?.();
    }
  }, [props]);

  const onRiveEvent = useCallback(
    (event: Event) => {
      const data = (event?.data || {}) as RiveEventPayload;
      if (data.name && (data.name === 'finish' || data.name === 'close_animation_completed')) {
        onClose();
      }
    },
    [onClose],
  );

  const { rive, RiveComponent } = useRiveMachine({
    src: signUpEmailRiv,
    artboard: ARTBOARD_NAME,
    stateMachineName: STATE_MACHINE_NAME,
    autoplay: false,
    layout: new Layout({
      fit: Fit.Contain,
    }),
    loadingTimeout: RIVE_FALLBACK_TIMEOUT_MS,
    onLoadError: () => {
      if (isRiveStaticFallbackEnabled) {
        setShowStaticFallback(true);
      } else {
        onClose();
      }
    },
  });

  useEffect(() => {
    if (rive && props.isCurrent) {
      rive.off(EventType.RiveEvent, onRiveEvent);
      rive.on(EventType.RiveEvent, onRiveEvent);
      rive.setTextRunValue('success', t('dialogs.successLowerCase'));
      rive.setTextRunValue('yourEmail', t('auth.emailVerifiedSuccessfully'));
      rive.play();

      const timeoutId = window.setTimeout(onClose, AUTO_CLOSE_DELAY_MS);
      return () => {
        window.clearTimeout(timeoutId);
        rive.off(EventType.RiveEvent, onRiveEvent);
      };
    }
  }, [rive, onRiveEvent, onClose, props.isCurrent, t]);

  if (showStaticFallback) {
    return (
      <div className={styles.container} data-testid="email-verification-success">
        <EmailVerificationSuccessStaticContent closeDelay={AUTO_CLOSE_DELAY_MS} onClose={onClose} />
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="email-verification-success">
      <RiveComponent className={styles.rive} />
    </div>
  );
});
